<template>
  <section
    class="container-fluid partners-certificates-section py-5 bg-light"
    data-aos="zoom-in"
    data-aos-duration="6000"
  >
    <div class="container">
      <div class="row justify-content-center">
        <!-- PARTNERS (CON ESCALONADO, 1 FILA EN DESKTOP/LAPTOP) -->
        <div class="col-12 text-center mb-5">
          <h2 class="pc-title mb-4">{{ titles.partners[selectedLang] }}</h2>

          <div
            class="pc-row"
            ref="pcRow"
            :style="{ gap: partnerGap + 'px' }"
          >
            <div
              v-for="(partner, idx) in partnersEscalonados"
              :key="'partner-' + partner.id"
              class="pc-img-link"
              :style="partnerStyle(idx)"
              title="Abrir sitio del partner"
            >
              <a :href="partner.url" target="_blank" rel="noopener" :aria-label="partner.alt">
                <img :src="partner.img" :alt="partner.alt" class="pc-img" />
              </a>
            </div>
          </div>
        </div>

        <!-- CERTIFICACIONES (2 FILAS, SIN ESCALONADO, SOLO IMÁGENES) -->
        <div class="col-12 text-center">
          <h2 class="pc-title mb-4">{{ titles.certifications[selectedLang] }}</h2>

          <div class="pc-grid-cert">
            <div
              v-for="cert in certsList"
              :key="'cert-' + cert.id"
              class="pc-img-link cert-card"
            >
              <!-- Se elimina el <a>, solo se deja la imagen -->
              <img :src="cert.img" :alt="cert.alt" class="pc-img cert-img" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { certifications, partners } from '@/assets/data/data.ts'
import AOS from 'aos'
import 'aos/dist/aos.css'

export default {
  name: 'PartnersCertificates',
  props: { selectedLang: { type: String, default: 'es' } },
  data () {
    return {
      vw: 0,
      rowWidth: 0,
      partnerGap: 16,
      partnerSizes: [], // tamaños calculados (px) por partner
      titles: {
        certifications: { es: 'Certificaciones', en: 'Certifications', de: 'Zertifizierungen' },
        partners: { es: 'Partners', en: 'Partners', de: 'Partner' }
      }
    }
  },
  computed: {
    partnersEscalonados () {
      const arr = partners || []
      const l = arr.length
      if (l <= 1) return arr
      const result = Array(l)
      const center = Math.floor((l - 1) / 2)
      let left = center, right = center + 1, idx = 0
      while (left >= 0 || right < l) {
        if (left >= 0) result[left--] = arr[idx++]
        if (right < l && idx < arr.length) result[right++] = arr[idx++]
      }
      return result
    },
    certsList () {
      return certifications || []
    }
  },
  methods: {
    // Llama en mount y resize
    onResize () {
      this.vw = typeof window !== 'undefined' ? window.innerWidth : 1200
      // gap por breakpoint
      if (this.vw >= 1400) this.partnerGap = 18
      else if (this.vw >= 1200) this.partnerGap = 16
      else if (this.vw >= 992) this.partnerGap = 14
      else if (this.vw >= 768) this.partnerGap = 12
      else this.partnerGap = 10

      this.$nextTick(() => {
        const el = this.$refs.pcRow
        this.rowWidth = el ? el.clientWidth : 1100
        this.computePartnerSizes()
      })
    },

    // Calcula tamaños para que TODOS quepan en 1 fila (≥992px)
    computePartnerSizes () {
      const N = this.partnersEscalonados.length
      if (!N || this.rowWidth <= 0) {
        this.partnerSizes = []
        return
      }

      // En tablet/móvil dejo que se envuelva, no fuerzo tamaños exactos
      const desktop = this.vw >= 992
      // Límites por breakpoint
      let minS, maxS, alpha
      if (this.vw >= 1400) { minS = 120; maxS = 220; alpha = 0.35 }
      else if (this.vw >= 1200) { minS = 105; maxS = 190; alpha = 0.32 }
      else if (this.vw >= 992) { minS = 95; maxS = 165; alpha = 0.30 }
      else if (this.vw >= 768) { minS = 90; maxS = 140; alpha = 0.28 }
      else { minS = 86; maxS = 128; alpha = 0.25 }

      if (!desktop) {
        // tamaños “comodín” para que el wrap se vea bien
        const base = Math.max(minS, Math.min(140, (this.rowWidth - this.partnerGap) / 3))
        this.partnerSizes = Array(N).fill(base)
        return
      }

      const free = this.rowWidth - this.partnerGap * (N - 1)
      const center = (N - 1) / 2

      // pesos escalonados (centro = mayor)
      const weights = []
      for (let i = 0; i < N; i++) {
        const dist = Math.abs(i - center)
        const w = 1 + alpha * (center - dist) // >= 1
        weights.push(w)
      }
      const sumW = weights.reduce((a, b) => a + b, 0)
      let base = free / sumW

      // Primer pase
      let sizes = weights.map(w => base * w)

      // Clamp y pequeña corrección para evitar overflow
      sizes = sizes.map(s => Math.max(minS, Math.min(maxS, s)))

      // Si aún así nos pasamos por redondeos, escalamos todo
      const used = sizes.reduce((a, b) => a + b, 0) + this.partnerGap * (N - 1)
      if (used > this.rowWidth) {
        const scale = (this.rowWidth - this.partnerGap * (N - 1)) / (used - this.partnerGap * (N - 1))
        sizes = sizes.map(s => Math.max(minS, s * scale))
      }
      this.partnerSizes = sizes
    },

    partnerStyle (idx) {
      const size = (this.partnerSizes && this.partnerSizes[idx]) || 120
      const px = size + 'px'
      return {
        height: px,
        width: px,
        padding: Math.round(size * 0.08) + 'px',
        borderRadius: '16px',
        border: '1px solid #ddd',
        boxShadow: '0 2px 8px #00000011',
        background: '#fff',
        transition: 'all .2s'
      }
    }
  },
  mounted () {
    this.onResize()
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onResize, { passive: true })
    }
    AOS.init({ duration: 800, once: true })
    setTimeout(() => AOS.refresh(), 300)
  },
  beforeUnmount () {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize)
    }
  }
}
</script>

<style scoped>
.pc-title {
  font-size: 2.2rem;
  font-weight: 400;
  color: #222;
  transition: color 0.3s ease;
  display: inline-block;
  position: relative;
}
.pc-title::after {
  content: '';
  position: absolute;
  left: 0; bottom: 0; height: 2px; width: 0%;
  background-color: currentColor; transition: width 0.3s ease;
}
.pc-title:hover::after { width: 100%; }

/* PARTNERS */
.pc-row {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  /* Clave: 1 sola fila en ≥992px */
  flex-wrap: nowrap;
}
@media (max-width: 991.98px) {
  .pc-row {
    flex-wrap: wrap;             /* en tablet/móvil sí se puede envolver */
    justify-content: center;
  }
}

.pc-img-link { display: flex; align-items: center; justify-content: center; }
.pc-img-link:hover .pc-img {
  transform: scale(1.06) rotate(-1.5deg);
  box-shadow: 0 4px 14px #11668718;
}

.pc-img {
  object-fit: cover;
  width: 100%; height: 100%;
  max-height: 90%;
  border: 0; background: transparent;
  transition: all .19s;
  display: block;
}

/* CERTIFICACIONES (2 filas, uniformes) */
.pc-grid-cert {
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: repeat(2, minmax(72px, 1fr));
  grid-auto-columns: minmax(120px, 1fr);
  column-gap: clamp(14px, 2.2vw, 28px);
  row-gap: clamp(18px, 3.2vw, 36px);
  justify-content: center;
  align-content: center;
  max-width: 1100px;
  margin: 0 auto;
  overflow: hidden;
}
.cert-card {
  width: clamp(120px, 13vw, 160px);
  height: clamp(66px, 8vw, 100px);
  padding: clamp(6px, .9vw, 10px);
  border: 1px solid #e9eef4;
  border-radius: 14px;
  box-shadow: 0 3px 12px rgba(15, 44, 84, 0.08);
  background: linear-gradient(180deg, #fff, #fafcfe);
  overflow: hidden;
}
.cert-img {
  width: 100%; height: 100%; max-height: 90%;
  object-fit: contain;
  filter: saturate(1.03) contrast(1.06);
  opacity: .98;
}
.cert-card:hover .cert-img { transform: scale(1.04); opacity: 1; }

/* Responsive titulos */
@media (max-width: 991.98px) { .pc-title { font-size: 1.6rem; } }
@media (max-width: 767.98px) {
  .pc-title { font-size: 1.4rem; }
  .pc-grid-cert {
    grid-auto-flow: row;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: auto;
    column-gap: 14px; row-gap: 16px; max-width: 560px;
  }
  .cert-card { width: 100%; height: clamp(64px, 18vw, 92px); padding: 8px; }
}
</style>
